import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Paper, Tab, Tabs, Typography } from '@mui/material';
import { Refresh as RefreshIcon, Storage as RegisterIcon } from '@mui/icons-material';
import {
  useTable,
  type RowSelectionState,
  type SortingState,
} from '@tanstack/react-table';
import { features } from '../components/common/tableFeatures';
import { useSnackbar } from 'notistack';
import errorService from '@services/errorService';
import {
  useGetDfoGatewaysQuery,
  useRegisterDfoGatewaysMutation,
  type DfoGateway,
} from '@store/api/dfoRegistryApi';
import { useQueryError } from '@hooks/useQueryError';
import { DataTable } from '@components/common/DataTable';
import { PENDING_COLUMNS, REGISTERED_COLUMNS } from '@components/dfo/dfoRegistryColumns';

// Stable empty array: a fresh `[]` per render would make the table recompute forever.
const NO_GATEWAYS: DfoGateway[] = [];

type TabValue = 'pending' | 'registered';
const NEWEST_FIRST: SortingState = [{ id: 'dfo_registered_at', desc: true }];

const DfoRegistryPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<TabValue>('pending');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

  const { data, isFetching, error: fetchError, refetch } = useGetDfoGatewaysQuery();
  const [registerGateways, { isLoading: registering }] = useRegisterDfoGatewaysMutation();
  const queryError = useQueryError(fetchError);

  const configured = data?.configured ?? false;
  const [pending, registered] = useMemo(() => {
    const gateways = data?.data ?? NO_GATEWAYS;
    return [
      gateways.filter((g) => !g.dfo_registered_at),
      gateways.filter((g) => g.dfo_registered_at),
    ];
  }, [data]);

  const view = {
    pending: {
      data: pending,
      columns: PENDING_COLUMNS,
      label: 'DFO gateways awaiting registration',
      empty: 'No DFO gateways waiting for registration',
    },
    registered: {
      data: registered,
      columns: REGISTERED_COLUMNS,
      label: 'Registered DFO gateways',
      empty: 'No DFO gateways registered yet',
    },
  }[tab];

  const table = useTable({
    features,
    data: view.data,
    columns: view.columns,
    state: { sorting, rowSelection },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getRowId: (row) => row.device_id,
  });

  const handleTabChange = (value: TabValue) => {
    setTab(value);
    setRowSelection({});
    setSorting(value === 'registered' ? NEWEST_FIRST : []);
  };

  const selectedIds = table.getSelectedRowModel().rows.map((row) => row.original.device_id);

  const handleRegister = async () => {
    try {
      const result = await registerGateways({ device_ids: selectedIds }).unwrap();
      enqueueSnackbar(`${result.successful} registered, ${result.failed} failed`, {
        variant: result.failed > 0 ? 'warning' : 'success',
      });
    } catch (err) {
      enqueueSnackbar(errorService.toUserMessage(err), { variant: 'error' });
    }
    setRowSelection({});
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">DFO Registry</Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </Button>
          {tab === 'pending' && (
            <Button
              variant="contained"
              startIcon={<RegisterIcon />}
              onClick={handleRegister}
              disabled={!configured || selectedIds.length === 0 || registering}
            >
              Register selected ({selectedIds.length})
            </Button>
          )}
        </Box>
      </Box>

      {queryError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {queryError}
        </Alert>
      )}

      {data && !configured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          The DFO database connection is not configured (DFO_SQL_* settings), so gateways cannot be
          registered.
        </Alert>
      )}

      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mb: 2
        }}>
        DFO gateways with a secure code. Registering writes them to the DFO Azure SQL database;
        registered gateways move to the Registered tab.
      </Typography>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, value) => handleTabChange(value)}
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 40,
            px: 1,
            '& .MuiTab-root': { minHeight: 40, py: 0, textTransform: 'none', fontSize: '0.875rem' },
          }}
        >
          <Tab label={`Pending (${pending.length})`} value="pending" />
          <Tab label={`Registered (${registered.length})`} value="registered" />
        </Tabs>
        <DataTable
          table={table}
          ariaLabel={view.label}
          isLoading={isFetching}
          emptyMessage={view.empty}
        />
      </Paper>
    </Box>
  );
};

export default DfoRegistryPage;
